import {
    Box,
    Button,
    Dialog,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import { ArrowBack, PersonSearch, Search } from "@mui/icons-material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomerCreationWizard from "../../components/Customer/CustomerCreationWizard";
import { PosContext } from "../../shared/PosContext";
import { useGetDocTypeList } from "../../shared/hooks/useDocTypesData";
import { dateFormat, dateTimeFormat } from "../../utils/dateUtils";
import { getOrderShortName, parseAddress, parseCustomerAddress } from "../../utils/floorUtils";
import OrderDetailsDialog from "../PosPage/components/OrderDetailsDialog";

function TabPanel({ value, index, children }) {
    if (value !== index) return null;
    return <Box sx={{ pt: 2 }}>{children}</Box>;
}

const stringifyAddresses = (addressMap, customerName) => {
    if (!addressMap || !customerName) return "";
    const list = addressMap[customerName] || [];
    return list.map((a) => `${a.address_line1 || ""} ${a.address_line2 || ""} ${a.city || ""}`).join(" ");
};

export default function CustomerBrowserDialog({ open, onClose }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { customers, addresses, refetchCustomers } = useContext(PosContext);

    const [searchText, setSearchText] = useState("");
    const [selectedCustomerName, setSelectedCustomerName] = useState(null);
    const [selectedAddress, setSelectedAddress] = useState("");

    const [tab, setTab] = useState(0);
    const [createOpen, setCreateOpen] = useState(false);

    const [historyDetailsOpen, setHistoryDetailsOpen] = useState(false);
    const [historyOrderName, setHistoryOrderName] = useState(null);

    useEffect(() => {
        if (open) {
            setSearchText("");
            setSelectedCustomerName(null);
            setSelectedAddress("");
            setTab(0);
        }
    }, [open]);

    const selectedCustomer = useMemo(() => {
        if (!selectedCustomerName || !customers) return null;
        return customers.find((c) => c.name === selectedCustomerName) || null;
    }, [customers, selectedCustomerName]);

    const addressOptions = useMemo(() => {
        if (!selectedCustomerName || !addresses) return [];
        return addresses[selectedCustomerName] || [];
    }, [addresses, selectedCustomerName]);

    const filteredCustomers = useMemo(() => {
        const list = customers || [];
        const q = String(searchText || "").trim().toLowerCase();
        if (!q) return list;

        return list.filter((c) => {
            const name = String(c.customer_name || "").toLowerCase();
            const mobile = String(c.mobile_no || "").toLowerCase();
            const email = String(c.email_id || "").toLowerCase();
            const addr1 = String(parseCustomerAddress(c) || "").toLowerCase();
            const addr2 = String(stringifyAddresses(addresses, c.name) || "").toLowerCase();
            return (
                name.includes(q) ||
                mobile.includes(q) ||
                email.includes(q) ||
                addr1.includes(q) ||
                addr2.includes(q)
            );
        });
    }, [customers, addresses, searchText]);

    const { data: customerOrders, isLoading: isLoadingOrders } = useGetDocTypeList({
        doctype: "Table Order",
        filters: selectedCustomerName
            ? [["customer", "=", selectedCustomerName]]
            : [["name", "=", "__none__"]],
        fields: ["name", "creation", "amount", "status"],
        sortBy: "creation desc",
        limit: 30,
    });

    const close = () => onClose?.();

    const openCreateCustomer = () => setCreateOpen(true);
    const closeCreateCustomer = () => setCreateOpen(false);

    const onCustomerCreated = (customerName) => {
        refetchCustomers?.();
        setCreateOpen(false);
        if (customerName) {
            setSelectedCustomerName(customerName);
            setTab(0);
        }
    };

    const createNewOrder = () => {
        if (!selectedCustomerName) return;
        const qs = new URLSearchParams();
        qs.set("customer", selectedCustomerName);
        if (selectedAddress) qs.set("address", selectedAddress);
        navigate(`/pos/takeaway?${qs.toString()}`);
        close();
    };

    const openOrderDetails = (orderName) => {
        setHistoryOrderName(orderName);
        setHistoryDetailsOpen(true);
    };

    return (
        <>
            <Dialog open={open} onClose={close} fullWidth maxWidth="md">
                <Box sx={{ p: 2, display: "flex", flexDirection: "column", minHeight: 520 }}>
                    {/* Header */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                            {selectedCustomerName ? (
                                <IconButton
                                    onClick={() => {
                                        setSelectedCustomerName(null);
                                        setSelectedAddress("");
                                        setTab(0);
                                    }}
                                >
                                    <ArrowBack />
                                </IconButton>
                            ) : (
                                <PersonSearch />
                            )}
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 900 }}>
                                    {selectedCustomerName
                                        ? selectedCustomer?.customer_name || selectedCustomerName
                                        : t("customerBrowser.title")}
                                </Typography>
                                {selectedCustomerName ? (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#6E7187",
                                            fontWeight: 700,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {selectedCustomer?.mobile_no || ""}
                                    </Typography>
                                ) : null}
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1 }}>
                            {!selectedCustomerName ? (
                                <Button variant="contained" onClick={openCreateCustomer}>
                                    {t("customerBrowser.newCustomer")}
                                </Button>
                            ) : (
                                <Button variant="contained" onClick={createNewOrder}>
                                    {t("customerBrowser.createNewOrder")}
                                </Button>
                            )}
                            <Button variant="outlined" onClick={close}>
                                {t("common.close")}
                            </Button>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    {/* Customer list view */}
                    {!selectedCustomerName ? (
                        <>
                            <TextField
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder={t("customerBrowser.searchPlaceholder")}
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Box sx={{ flex: 1, overflowY: "auto", mt: 1 }}>
                                <List dense>
                                    {filteredCustomers.map((c) => (
                                        <ListItemButton
                                            key={c.name}
                                            onClick={() => {
                                                setSelectedCustomerName(c.name);
                                                setTab(0);
                                            }}
                                        >
                                            <ListItemText
                                                primary={c.customer_name}
                                                secondary={`${c.mobile_no || ""}${
                                                    c.email_id ? ` • ${c.email_id}` : ""
                                                }${parseCustomerAddress(c) ? ` • ${parseCustomerAddress(c)}` : ""}`}
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                                <Tab label={t("customerBrowser.ordersHistory")} />
                                <Tab label={t("customerBrowser.customerDetails")} />
                                <Tab label={t("customerBrowser.addressForNewOrder")} />
                            </Tabs>

                            <Divider sx={{ mt: 1 }} />

                            <TabPanel value={tab} index={0}>
                                <List dense>
                                    {!isLoadingOrders &&
                                        (customerOrders || []).map((o) => {
                                            const created = o.creation || "";
                                            const createdLabel = created
                                                ? `${dateFormat(created)
                                                      .split("/")
                                                      .slice(0, 2)
                                                      .join("/")} - ${dateTimeFormat(
                                                      created.split(" ")[1] || ""
                                                  )}`
                                                : "";
                                            return (
                                                <ListItemButton
                                                    key={o.name}
                                                    onClick={() => openOrderDetails(o.name)}
                                                >
                                                    <ListItemText
                                                        primary={`#${getOrderShortName(o.name)} • ${o.amount || 0}₪`}
                                                        secondary={`${createdLabel}${
                                                            o.status ? ` • ${o.status}` : ""
                                                        }`}
                                                    />
                                                </ListItemButton>
                                            );
                                        })}
                                    {isLoadingOrders ? (
                                        <Typography sx={{ color: "#6E7187" }}>
                                            {t("common.loading")}
                                        </Typography>
                                    ) : null}
                                </List>
                            </TabPanel>

                            <TabPanel value={tab} index={1}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    <Typography sx={{ fontWeight: 900 }}>
                                        {selectedCustomer?.customer_name || selectedCustomerName}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#6E7187", fontWeight: 700 }}>
                                        {t("customerBrowser.phone")}: {selectedCustomer?.mobile_no || "—"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#6E7187", fontWeight: 700 }}>
                                        {t("customerBrowser.email")}: {selectedCustomer?.email_id || "—"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#6E7187", fontWeight: 700 }}>
                                        {t("customerBrowser.address")}: {selectedCustomer ? parseCustomerAddress(selectedCustomer) : "—"}
                                    </Typography>
                                </Box>
                            </TabPanel>

                            <TabPanel value={tab} index={2}>
                                <Typography sx={{ color: "#6E7187", fontWeight: 700, mb: 1 }}>
                                    {t("customerBrowser.chooseAddress")}
                                </Typography>
                                <List dense>
                                    <ListItemButton
                                        selected={!selectedAddress}
                                        onClick={() => setSelectedAddress("")}
                                    >
                                        <ListItemText primary={t("customerBrowser.noAddress")} />
                                    </ListItemButton>
                                    {addressOptions.map((a) => (
                                        <ListItemButton
                                            key={a.name}
                                            selected={selectedAddress === a.name}
                                            onClick={() => setSelectedAddress(a.name)}
                                        >
                                            <ListItemText primary={parseAddress(a) || a.name} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </TabPanel>
                        </>
                    )}
                </Box>
            </Dialog>

            <Dialog open={createOpen} onClose={closeCreateCustomer}>
                <CustomerCreationWizard onClose={closeCreateCustomer} onConfirm={onCustomerCreated} />
            </Dialog>

            <OrderDetailsDialog
                isOpen={historyDetailsOpen}
                selectedCustomer={selectedCustomerName}
                title={t("customerBrowser.orderDetails")}
                toggle={() => setHistoryDetailsOpen(false)}
                orderName={historyOrderName}
            />
        </>
    );
}


